import React, { useEffect, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import App from './app/app';
import { ADMIN_DOMAIN, COGNITO, SUPPORT_EMAIL, cognitoAuthority } from '@shared/lib/constants';
import { isSigningOut, setAuthToken } from './lib/authToken';
import { fetchUserRobots } from './lib/robots-api';
import { fetchMe, fetchOrgs, fetchRobotByName, VERNE_ORG_ID } from './lib/orgs-api';
import { useRobotStore } from './stores/useRobotStore';
import { useOrgStore, useEffectiveOrgId } from './stores/useOrgStore';
import { initDatadog, setDatadogUser } from './lib/datadog';
import { isKiosk, bootstrapKioskRobot } from './lib/kioskMode';
import './styles/globals.css';

console.info(`[dashboard] commit ${__GIT_COMMIT_HASH__}`);

initDatadog();

const REDIRECT_KEY = 'user_auth_redirect';

// Clear stale oidc-client-ts authorization state from sessionStorage on startup.
// These are orphaned entries from silent renew requests that were never completed
// (e.g. left by old code that had automaticSilentRenew: true). If left in place,
// they cause Cognito to return a 400 when Google sends back a prompt=none callback
// whose state no longer matches an active Cognito session.
for (const key of Object.keys(sessionStorage)) {
  if (key.startsWith('oidc.')) sessionStorage.removeItem(key);
}

const oidcConfig = {
  authority: cognitoAuthority(),
  client_id: COGNITO.userClientId,
  redirect_uri: window.location.origin,
  scope: 'email openid',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: false,
  monitorSession: false,
  onSigninCallback: () => {
    const saved = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
    // Restore deep link (hash route + ?robot_id query param) or clear OIDC callback params.
    window.history.replaceState({}, document.title, saved ?? window.location.pathname);
  },
};

// Look up which org owns the robot whose serial is in the URL's `robot_id`.
// The hub endpoint auth-checks the caller, so a non-verne user trying to
// load a deep link from another org gets a null here and we'll just drop
// the param.
async function resolveRobotOrg(robotId: string): Promise<number | null> {
  const robot = await fetchRobotByName(robotId);
  return robot?.org_id ?? null;
}

function AuthGate({ children }: { children: ReactNode }) {
  const auth = useAuth();

  useEffect(() => {
    if (auth.error) {
      console.error('[auth] error:', auth.error.message);
      return;
    }
    if (!auth.isLoading && !auth.isAuthenticated && !isSigningOut()) {
      // Preserve the full URL (pathname + ?robot_id=... + #/route) across the Cognito round-trip.
      const url = window.location.pathname + window.location.search + window.location.hash;
      const hasState = window.location.search || window.location.hash.length > 1 || (window.location.pathname !== '/' && window.location.pathname.length > 1);
      if (hasState) sessionStorage.setItem(REDIRECT_KEY, url);
      // prompt=login forces Cognito to do a fresh login instead of using its session
      // cookie to silently re-auth (which passes prompt=none to Google and 400s when
      // blocked by the pre-signup Lambda — with no callback URL for the error to reach us).
      auth.signinRedirect({ extraQueryParams: { identity_provider: 'Google' }, prompt: 'login' });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error]);

  // Keep token store in sync whenever the OIDC user object updates.
  useEffect(() => {
    if (auth.user?.id_token) setAuthToken(auth.user.id_token);
  }, [auth.user?.id_token]);

  // Load /me once authenticated; it embeds the user's org so non-verne users
  // need nothing more. Verne admins additionally fetch /orgs to populate the
  // switcher. Best-effort — failure leaves the org store empty and the slot
  // hidden.
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.id_token) return;
    let cancelled = false;
    fetchMe().then((me) => {
      if (cancelled || !me) return;
      useOrgStore.getState().setMe(me);
      if (me.org_id === VERNE_ORG_ID) {
        fetchOrgs().then((orgs) => {
          if (cancelled || !orgs) return;
          useOrgStore.getState().setOrgs(orgs);
        });
      } else if (me.org) {
        // Single-org seed so the read-only badge can resolve a name.
        useOrgStore.getState().setOrgs([me.org]);
      }
    });
    return () => { cancelled = true; };
  }, [auth.isAuthenticated, auth.user?.id_token]);

  // Identify the user in Datadog RUM.
  // usr.name must be set or the Sessions UI labels the session "Public User"
  // even when usr.id and usr.email are present.
  useEffect(() => {
    const profile = auth.user?.profile;
    if (auth.isAuthenticated && profile) {
      const email = typeof profile.email === 'string' ? profile.email : undefined;
      const given = typeof profile.given_name === 'string' ? profile.given_name : undefined;
      const family = typeof profile.family_name === 'string' ? profile.family_name : undefined;
      const fullName = typeof profile.name === 'string' ? profile.name : undefined;
      const composed = [given, family].filter(Boolean).join(' ') || undefined;
      const name = fullName ?? composed ?? email;
      setDatadogUser({
        id: String(profile.sub ?? profile.email ?? ''),
        email,
        name,
      });
    } else if (!auth.isAuthenticated && !auth.isLoading) {
      setDatadogUser(null);
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.user?.profile]);

  if (auth.error) {
    const msg = auth.error.message ?? '';
    const isPendingApproval = msg.toLowerCase().includes('pending approval') || msg.toLowerCase().includes(ADMIN_DOMAIN);

    if (isPendingApproval) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground gap-6 px-6 text-center">
          <div className="flex flex-col items-center gap-3 max-w-sm">
            <div className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
            </div>
            <h1 className="text-base font-semibold">Access Pending Approval</h1>
            <p className="text-sm text-muted leading-relaxed">
              Your account hasn't been approved yet. Please reach out to Verne to request access.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-sm font-medium text-accent hover:opacity-80 transition-opacity"
            >
              {SUPPORT_EMAIL}
            </a>
          </div>
          <button
            onClick={() => { auth.removeUser(); window.location.href = window.location.origin; }}
            className="px-4 py-2 rounded-lg border border-border bg-surface text-sm text-muted hover:text-foreground hover:bg-surface-raised transition-colors"
          >
            Try a different account
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground gap-4">
        <p className="text-sm text-red-400">Something went wrong. Please try again.</p>
        <button
          onClick={() => { auth.removeUser(); window.location.href = window.location.origin; }}
          className="px-4 py-2 rounded-lg border border-border bg-surface text-sm hover:bg-surface-raised transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-background text-muted text-sm">
        {isSigningOut() ? 'Signing out…' : auth.isLoading ? 'Signing in…' : 'Redirecting to login…'}
      </div>
    );
  }

  return <>{children}</>;
}

function RobotsGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const setFleet = useRobotStore((s) => s.setFleet);
  const setFleetLoading = useRobotStore((s) => s.setFleetLoading);
  const fleet = useRobotStore((s) => s.fleet);
  const fleetLoading = useRobotStore((s) => s.fleetLoading);

  const idToken = auth.user?.id_token;
  const orgId = useEffectiveOrgId();

  useEffect(() => {
    if (!idToken || orgId == null) return;
    let cancelled = false;

    const load = (attempt = 0) => {
      setFleetLoading(true);
      fetchUserRobots(orgId, idToken)
        .then((list) => {
          if (cancelled) return;
          setFleet(list);
          setFleetLoading(false);
          // Re-read robot_id from the live URL rather than the module-cached
          // `currentRobotId`. The org selector kicks off `location.replace`
          // to drop the param synchronously, but this fetch was started by
          // the same React render that triggered the selector and may
          // resolve before the page unloads. Trusting the cached value
          // would run cross-org inference on the *previous* org's robot
          // and bounce the user right back.
          const liveRobotId = new URLSearchParams(window.location.search).get('robot_id') ?? null;
          if (list.length > 0 && !liveRobotId) {
            const url = new URL(window.location.href);
            url.searchParams.set('robot_id', list[0].id);
            window.location.replace(url.toString());
            return;
          }
          // ?robot_id= points at a robot that's not in this org's fleet —
          // either a deep link from another org (verne admins) or a stale
          // link to a robot the user no longer has access to. Try to find
          // the owning org and switch to it; otherwise drop the param.
          if (liveRobotId && !list.some((r) => r.id === liveRobotId)) {
            void resolveRobotOrg(liveRobotId).then((targetOrgId) => {
              if (cancelled) return;
              if (targetOrgId != null && targetOrgId !== orgId) {
                useOrgStore.getState().setSelectedOrgId(targetOrgId);
                window.location.reload();
              } else {
                const url = new URL(window.location.href);
                url.searchParams.delete('robot_id');
                window.location.replace(url.toString());
              }
            });
          }
        })
        .catch(() => {
          if (cancelled) return;
          // Retry up to 3 times with backoff before giving up.
          if (attempt < 3) {
            setTimeout(() => load(attempt + 1), 1000 * 2 ** attempt);
          } else {
            setFleet([]);
            setFleetLoading(false);
          }
        });
    };

    load();
    return () => { cancelled = true; };
  }, [idToken, orgId]);

  if (!fleetLoading && fleet.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground gap-6 px-6 text-center">
        <div className="flex flex-col items-center gap-3 max-w-sm">
          <div className="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted">
              <rect x="3" y="11" width="18" height="10" rx="2" />
              <path d="M12 11V7" />
              <circle cx="12" cy="5" r="2" />
              <path d="M7 15h.01M17 15h.01" />
            </svg>
          </div>
          <h1 className="text-base font-semibold">No Devices</h1>
          <p className="text-sm text-muted leading-relaxed">
            Your account doesn't have access to any devices yet. Please reach out to Verne to get set up.
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-sm font-medium text-accent hover:opacity-80 transition-opacity"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
        <button
          onClick={() => { auth.removeUser(); window.location.href = window.location.origin; }}
          className="px-4 py-2 rounded-lg border border-border bg-surface text-sm text-muted hover:text-foreground hover:bg-surface-raised transition-colors"
        >
          Try a different account
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

const root = ReactDOM.createRoot(document.getElementById('root')!);

if (isKiosk) {
  // Local kiosk: skip Cognito redirect + RobotsGate, talk to local FastAPI
  // directly. Still wrap with <AuthProvider> so descendants that call
  // useAuth() (e.g. UserMenu) get a real context — they just see
  // isAuthenticated=false, user=undefined, and render their unauth fallback.
  setAuthToken('');

  // Best-effort hydrate of /me + /orgs via the local auth proxy. Failure is
  // silent so the dashboard stays usable when the proxy (or upstream cloud)
  // is offline.
  void fetchMe().then((me) => {
    if (!me) return;
    useOrgStore.getState().setMe(me);
    if (me.org_id === VERNE_ORG_ID) {
      void fetchOrgs().then((orgs) => {
        if (orgs) useOrgStore.getState().setOrgs(orgs);
      });
    } else if (me.org) {
      useOrgStore.getState().setOrgs([me.org]);
    }
  });

  bootstrapKioskRobot()
    .then(() => {
      root.render(
        <React.StrictMode>
          <AuthProvider {...oidcConfig}>
            <App />
          </AuthProvider>
        </React.StrictMode>,
      );
    })
    .catch((err) => {
      console.error('[kiosk] bootstrap failed', err);
      root.render(
        <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground gap-3 px-6 text-center">
          <p className="text-sm text-red-400">Kiosk init failed: {String(err?.message ?? err)}</p>
          <button
            className="px-4 py-2 rounded-lg border border-border bg-surface text-sm hover:bg-surface-raised transition-colors"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>,
      );
    });
} else {
  root.render(
    <React.StrictMode>
      <AuthProvider {...oidcConfig}>
        <AuthGate>
          <RobotsGate>
            <App />
          </RobotsGate>
        </AuthGate>
      </AuthProvider>
    </React.StrictMode>,
  );
}
